import { ASSET_METADATA_LABELS } from '@/features/admin-dashboard/config/assetCatalog';
import type { AssetCatalogFilterState } from '@/features/admin-dashboard/types/assetCatalog';
import {
  ASSET_METADATA_FIELD_KEYS,
  type AssetCatalogFilters,
  type AssetMetadataFieldKey,
} from '@/features/assets/types/asset';
import type { I18n } from '@lingui/core';

export function getAssetMetadataFilterFields(i18n: I18n) {
  return ASSET_METADATA_FIELD_KEYS.map((key) => ({
    key,
    label: i18n._(ASSET_METADATA_LABELS[key]),
  }));
}

export function getAssetCatalogActiveFilterCounts(filters: AssetCatalogFilterState) {
  const general =
    Number(Boolean(filters.organizationId || filters.projectId)) +
    Number(filters.rejection !== 'all');
  const range = [
    filters.minDurationSeconds,
    filters.maxDurationSeconds,
    filters.minSizeMegabytes,
    filters.maxSizeMegabytes,
  ].filter((value) => value.trim()).length;
  const metadata = ASSET_METADATA_FIELD_KEYS.reduce(
    (count, key) =>
      count +
      Number(Boolean(filters.metadataValues[key]?.trim())) +
      Number((filters.metadataPresence[key] ?? 'any') !== 'any'),
    0,
  );

  return { general, range, metadata, total: general + range + metadata };
}

function toWireUnit(value: string, multiplier: number) {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * multiplier) : undefined;
}

function toMetadataValues(values: AssetCatalogFilterState['metadataValues']) {
  return Object.fromEntries(
    Object.entries(values).filter((entry): entry is [AssetMetadataFieldKey, string] =>
      Boolean(entry[1]?.trim()),
    ),
  );
}

function toMetadataPresence(values: AssetCatalogFilterState['metadataPresence']) {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([, presence]) => presence && presence !== 'any')
      .map(([key, presence]) => [key, presence === 'present']),
  );
}

export function toAssetCatalogRequestFilters(
  query: string,
  filters: AssetCatalogFilterState,
  durationMultiplier: number,
  sizeMultiplier: number,
): AssetCatalogFilters {
  return {
    q: query,
    organizationId: filters.organizationId || undefined,
    projectId: filters.projectId || undefined,
    minDurationMs: toWireUnit(filters.minDurationSeconds, durationMultiplier),
    maxDurationMs: toWireUnit(filters.maxDurationSeconds, durationMultiplier),
    minSizeBytes: toWireUnit(filters.minSizeMegabytes, sizeMultiplier),
    maxSizeBytes: toWireUnit(filters.maxSizeMegabytes, sizeMultiplier),
    metadata: toMetadataValues(filters.metadataValues),
    metadataPresent: toMetadataPresence(filters.metadataPresence),
  };
}
